import React from 'react';
import { Avatar, Box, Button, CircularProgress, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  fetchCharacters,
  setMainCharacter,
  startDirectorVerify,
  startLinkCharacter,
  startReauth,
  unlinkCharacter,
} from '../api/characters';
import ConsolePanel from '../components/ConsolePanel';
import StatusBadge from '../components/StatusBadge';
import { useDashboardPrefs } from '../hooks/useDashboardPrefs';
import { C, FONT, frameSx, revealSx } from '../theme/tokens';
import { CharacterSummary } from '../types';

const STATUS_COPY: Record<CharacterSummary['authStatus'], { label: string; color: string }> = {
  ok: { label: 'OK', color: C.cyan },
  needs_reauth: { label: 'Needs reauth', color: C.gold },
  refresh_failed: { label: 'Refresh failed', color: C.red },
};

/** Per-character account management: auth status, reauth, unlink, set-main,
 * and adding another character (a plain SSO trip — the WormholeGate ring is
 * exclusive to the pre-auth login screen now, not duplicated here). */
const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const reauthOk = searchParams.get('reauth') === '1';
  const directorVerifyDone = searchParams.get('directorVerify') === '1';
  const error = searchParams.get('error');

  const { data: characters, isLoading } = useQuery({ queryKey: ['characters'], queryFn: fetchCharacters });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['characters'] });

  const reauthMutation = useMutation({
    mutationFn: (id: number) => startReauth(id),
    onSuccess: ({ auth_url }) => {
      window.location.href = auth_url;
    },
  });

  const mainMutation = useMutation({ mutationFn: (id: number) => setMainCharacter(id), onSuccess: invalidate });

  const unlinkMutation = useMutation({ mutationFn: (id: number) => unlinkCharacter(id), onSuccess: invalidate });

  const linkMutation = useMutation({
    mutationFn: () => startLinkCharacter(),
    onSuccess: ({ auth_url }) => {
      window.location.href = auth_url;
    },
  });

  const directorVerifyMutation = useMutation({
    mutationFn: (id: number) => startDirectorVerify(id),
    onSuccess: ({ auth_url }) => {
      window.location.href = auth_url;
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress sx={{ color: C.blue }} />
      </Box>
    );
  }

  const onlyCharacter = (characters?.length ?? 0) <= 1;

  return (
    <Stack spacing={3} sx={revealSx()}>
      <Typography variant="h1" sx={{ color: C.text, fontFamily: FONT.display, fontSize: '1.7rem' }}>
        Settings
      </Typography>

      {reauthOk && (
        <Typography sx={{ color: C.cyan, fontFamily: FONT.mono, fontSize: '0.85rem' }}>
          &gt; Reauthentication successful.
        </Typography>
      )}
      {directorVerifyDone && (
        <Typography sx={{ color: C.cyan, fontFamily: FONT.mono, fontSize: '0.85rem' }}>
          &gt; In-game role check complete — see the character list below.
        </Typography>
      )}
      {error && (
        <Typography sx={{ color: C.red, fontFamily: FONT.mono, fontSize: '0.85rem' }}>
          &gt; {error === 'sso_failed' ? 'Reauthentication failed — try again.' : error}
        </Typography>
      )}

      <ConsolePanel
        title="Linked Characters"
        accent={C.cyan}
        action={
          <Button size="small" variant="outlined" onClick={() => linkMutation.mutate()}>
            Add Character
          </Button>
        }
      >
        <Stack spacing={1.5}>
          {(characters ?? []).map((c) => {
            const status = STATUS_COPY[c.authStatus];
            return (
              <Stack
                key={c.eve_character_id}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ ...frameSx(c.is_main ? C.blue : C.cyan), px: 2, py: 1.25 }}
              >
                <Avatar
                  variant="square"
                  src={`https://images.evetech.net/characters/${c.eve_character_id}/portrait?size=64`}
                  sx={{ width: 40, height: 40, borderRadius: '2px' }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ color: C.text, fontWeight: 600 }}>{c.name}</Typography>
                    {c.is_main && <StatusBadge label="Main" accent={C.blue} />}
                  </Stack>
                  <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.75rem' }}>
                    {c.corp_name ?? '—'}
                  </Typography>
                </Box>
                <StatusBadge label={status.label} accent={status.color} />
                {c.authStatus !== 'ok' && (
                  <Button size="small" onClick={() => reauthMutation.mutate(c.eve_character_id)} sx={{ fontSize: '0.7rem' }}>
                    Reauthenticate
                  </Button>
                )}
                {/* Proves this character's real in-game Director role to
                    ESI — an admin reviewing 'director' app grants sees this
                    same result on the Permission Grants tab. */}
                {c.esi_director_verified_at ? (
                  <Tooltip title={`Verified in-game Director role on ${new Date(c.esi_director_verified_at).toLocaleString()}`}>
                    <span>
                      <StatusBadge label="ESI Director" accent={C.gold} />
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    onClick={() => directorVerifyMutation.mutate(c.eve_character_id)}
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Verify Director role
                  </Button>
                )}
                {!c.is_main && (
                  <Button size="small" onClick={() => mainMutation.mutate(c.eve_character_id)} sx={{ fontSize: '0.7rem' }}>
                    Set as main
                  </Button>
                )}
                <Tooltip title={onlyCharacter ? 'Cannot unlink your only character' : ''}>
                  <span>
                    <Button
                      size="small"
                      disabled={onlyCharacter}
                      onClick={() => unlinkMutation.mutate(c.eve_character_id)}
                      sx={{ fontSize: '0.7rem', color: C.red }}
                    >
                      Unlink
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      </ConsolePanel>

      <DashboardPrefsPanel />
    </Stack>
  );
};

/** Layout preferences, stored per browser rather than per account — the same
 * call useSystemHistory makes, and for the same reason: this is a view
 * convenience, not account data. */
const DashboardPrefsPanel: React.FC = () => {
  const { prefs, set } = useDashboardPrefs();

  return (
    <ConsolePanel title="Dashboard" accent={C.cyan}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={prefs.showVaultPanel}
            onChange={(e) => set('showVaultPanel', e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography sx={{ fontSize: '0.85rem', color: C.text }}>Show Data Vault quick access</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: C.textDim, fontFamily: FONT.mono }}>
              Home systems and pins, below the character list. Saved in this browser only.
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', ml: 0 }}
      />
    </ConsolePanel>
  );
};

export default Settings;
